import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Clock, ArrowLeft, RefreshCw, Plus, Search, Play, Square, Pause,
  DollarSign, Target, TrendingUp, BarChart3, Filter, MoreHorizontal,
  Edit, Trash2, CheckCircle, FolderKanban, Users, Calendar, AlertCircle,
  Receipt, Settings2, ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { format, parseISO, formatDistanceToNow, differenceInSeconds } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  submitted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  approved: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  invoiced: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
};

const PROJECT_STATUS_COLORS = {
  active: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  on_hold: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  completed: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
};

const CHART_COLORS = ["hsl(var(--ff-primary))", "hsl(var(--ff-income))", "hsl(var(--ff-expense))", "hsl(var(--ff-warning))", "#8884d8", "#82ca9d"];

export default function TimeTrackingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("timer");
  
  // Data states
  const [runningTimer, setRunningTimer] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [utilization, setUtilization] = useState(null);
  const [unbilledTime, setUnbilledTime] = useState(null);
  const [settings, setSettings] = useState(null);
  
  // Timer display
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerIntervalRef = useRef(null);
  
  // Dialog states
  const [entryDialog, setEntryDialog] = useState({ open: false, entry: null });
  const [projectDialog, setProjectDialog] = useState({ open: false, project: null });
  const [settingsDialog, setSettingsDialog] = useState(false);
  
  // Form states
  const [timerForm, setTimerForm] = useState({
    description: "",
    client_id: "",
    project_id: "",
    is_billable: true,
    hourly_rate: ""
  });
  
  const [entryForm, setEntryForm] = useState({
    description: "",
    client_id: "",
    project_id: "",
    date: new Date().toISOString().split('T')[0],
    duration_minutes: 0,
    is_billable: true,
    hourly_rate: "",
    notes: ""
  });
  
  const [projectForm, setProjectForm] = useState({
    name: "",
    client_id: "",
    description: "",
    status: "active",
    budget_hours: "",
    budget_amount: "",
    hourly_rate: ""
  });
  
  const [settingsForm, setSettingsForm] = useState({
    default_hourly_rate: 100,
    target_weekly_hours: 40,
    target_utilization_percent: 80,
    default_billable: true,
    rounding_increment: 15
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [runningRes, entriesRes, projectsRes, clientsRes, settingsRes] = await Promise.all([
        api.get("/time-entries/running").catch(() => ({ data: null })),
        api.get("/time-entries?limit=50"),
        api.get("/projects"),
        api.get("/clients"),
        api.get("/time/settings")
      ]);
      
      setRunningTimer(runningRes.data);
      setTimeEntries(entriesRes.data);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setSettings(settingsRes.data);
      
      // Initialize settings form
      if (settingsRes.data) {
        setSettingsForm({
          default_hourly_rate: settingsRes.data.default_hourly_rate || 100,
          target_weekly_hours: settingsRes.data.target_weekly_hours || 40,
          target_utilization_percent: settingsRes.data.target_utilization_percent || 80,
          default_billable: settingsRes.data.default_billable !== false,
          rounding_increment: settingsRes.data.rounding_increment || 15
        });
      }
      
      // Start timer display if running
      if (runningRes.data?.start_time) {
        startTimerDisplay(runningRes.data.start_time);
      }
    } catch (error) {
      console.error("Error fetching time tracking data:", error);
      if (error.response?.status === 403) {
        toast.error("Time tracking is only available for service-based companies");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load time tracking data");
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const fetchUtilization = useCallback(async () => {
    try {
      const res = await api.get("/time/utilization?period=week");
      setUtilization(res.data);
    } catch (error) {
      console.error("Error fetching utilization:", error);
    }
  }, []);

  const fetchUnbilled = useCallback(async () => {
    try {
      const res = await api.get("/time/unbilled");
      setUnbilledTime(res.data);
    } catch (error) {
      console.error("Error fetching unbilled time:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === "reports") {
      fetchUtilization();
    } else if (activeTab === "unbilled") {
      fetchUnbilled();
    }
  }, [activeTab, fetchUtilization, fetchUnbilled]);

  const startTimerDisplay = (startTime) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    const start = typeof startTime === 'string' ? new Date(startTime) : startTime;
    
    const updateElapsed = () => {
      const now = new Date();
      const seconds = differenceInSeconds(now, start);
      setElapsedSeconds(seconds);
    };
    
    updateElapsed();
    timerIntervalRef.current = setInterval(updateElapsed, 1000);
  };

  const stopTimerDisplay = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setElapsedSeconds(0);
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatHoursMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleStartTimer = async () => {
    if (!timerForm.description.trim()) {
      toast.error("Please enter a task description");
      return;
    }
    
    try {
      const res = await api.post("/time-entries/start", {
        description: timerForm.description,
        client_id: timerForm.client_id || null,
        project_id: timerForm.project_id || null,
        is_billable: timerForm.is_billable,
        hourly_rate: timerForm.hourly_rate ? parseFloat(timerForm.hourly_rate) : null
      });
      
      setRunningTimer(res.data);
      startTimerDisplay(res.data.start_time);
      toast.success("Timer started");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start timer");
    }
  };

  const handleStopTimer = async () => {
    if (!runningTimer) return;
    
    try {
      await api.post(`/time-entries/${runningTimer.id}/stop`);
      stopTimerDisplay();
      setRunningTimer(null);
      setTimerForm({ description: "", client_id: "", project_id: "", is_billable: true, hourly_rate: "" });
      toast.success("Timer stopped");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to stop timer");
    }
  };

  const handleSaveEntry = async () => {
    if (!entryForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    
    try {
      const payload = {
        description: entryForm.description,
        client_id: entryForm.client_id || null,
        project_id: entryForm.project_id || null,
        date: entryForm.date,
        duration_minutes: parseInt(entryForm.duration_minutes) || 0,
        is_billable: entryForm.is_billable,
        hourly_rate: entryForm.hourly_rate ? parseFloat(entryForm.hourly_rate) : null,
        notes: entryForm.notes || null
      };
      
      if (entryDialog.entry?.id) {
        await api.put(`/time-entries/${entryDialog.entry.id}`, payload);
        toast.success("Time entry updated");
      } else {
        await api.post("/time-entries", payload);
        toast.success("Time entry created");
      }
      
      setEntryDialog({ open: false, entry: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save time entry");
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!confirm("Are you sure you want to delete this time entry?")) return;
    
    try {
      await api.delete(`/time-entries/${entryId}`);
      toast.success("Time entry deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete time entry");
    }
  };

  const handleSaveProject = async () => {
    if (!projectForm.name.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    
    try {
      const payload = {
        name: projectForm.name,
        client_id: projectForm.client_id || null,
        description: projectForm.description || null,
        status: projectForm.status,
        budget_hours: projectForm.budget_hours ? parseFloat(projectForm.budget_hours) : null,
        budget_amount: projectForm.budget_amount ? parseFloat(projectForm.budget_amount) : null,
        hourly_rate: projectForm.hourly_rate ? parseFloat(projectForm.hourly_rate) : null
      };
      
      if (projectDialog.project?.id) {
        await api.put(`/projects/${projectDialog.project.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created");
      }
      
      setProjectDialog({ open: false, project: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save project");
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm("Are you sure you want to delete this project?")) return;
    
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Project deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put("/time/settings", settingsForm);
      toast.success("Settings updated");
      setSettingsDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const openEntryDialog = (entry = null) => {
    if (entry) {
      setEntryForm({
        description: entry.description || "",
        client_id: entry.client_id || "",
        project_id: entry.project_id || "",
        date: entry.date ? entry.date.split('T')[0] : new Date().toISOString().split('T')[0],
        duration_minutes: entry.duration_minutes || 0,
        is_billable: entry.is_billable !== false,
        hourly_rate: entry.hourly_rate || "",
        notes: entry.notes || ""
      });
    } else {
      setEntryForm({
        description: "",
        client_id: "",
        project_id: "",
        date: new Date().toISOString().split('T')[0],
        duration_minutes: 0,
        is_billable: settings?.default_billable !== false,
        hourly_rate: settings?.default_hourly_rate || "",
        notes: ""
      });
    }
    setEntryDialog({ open: true, entry });
  };

  const openProjectDialog = (project = null) => {
    if (project) {
      setProjectForm({
        name: project.name || "",
        client_id: project.client_id || "",
        description: project.description || "",
        status: project.status || "active",
        budget_hours: project.budget_hours || "",
        budget_amount: project.budget_amount || "",
        hourly_rate: project.hourly_rate || ""
      });
    } else {
      setProjectForm({
        name: "",
        client_id: "",
        description: "",
        status: "active",
        budget_hours: "",
        budget_amount: "",
        hourly_rate: settings?.default_hourly_rate || ""
      });
    }
    setProjectDialog({ open: true, project });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b bg-card sticky top-0 z-10">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-48" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="time-tracking-page">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Clock className="h-6 w-6 text-[hsl(var(--ff-primary))]" />
              <div>
                <h1 className="font-semibold text-lg">Time Tracking</h1>
                <p className="text-xs text-muted-foreground">Track hours and manage projects</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setSettingsDialog(true)} data-testid="settings-btn">
              <Settings2 className="h-5 w-5" />
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-button">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* Running Timer Banner */}
        {runningTimer && (
          <Card className="border-[hsl(var(--ff-primary))]/50 bg-[hsl(var(--ff-primary))]/5" data-testid="running-timer-banner">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-[hsl(var(--ff-primary))] text-white animate-pulse">
                    <Play className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">{runningTimer.description}</p>
                    <p className="text-sm text-muted-foreground">
                      {runningTimer.client_name && `${runningTimer.client_name} • `}
                      {runningTimer.project_name && `${runningTimer.project_name} • `}
                      {runningTimer.is_billable ? "Billable" : "Non-billable"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-mono font-bold text-[hsl(var(--ff-primary))]">
                    {formatDuration(elapsedSeconds)}
                  </div>
                  <Button variant="destructive" size="lg" onClick={handleStopTimer} data-testid="stop-timer-btn">
                    <Square className="h-5 w-5 mr-2" />
                    Stop
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
            <TabsTrigger value="timer" data-testid="tab-timer">Timer</TabsTrigger>
            <TabsTrigger value="entries" data-testid="tab-entries">Time Entries</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="unbilled" data-testid="tab-unbilled">Unbilled</TabsTrigger>
          </TabsList>

          {/* Timer Tab */}
          <TabsContent value="timer" className="space-y-6">
            {/* Start Timer Card */}
            {!runningTimer && (
              <Card data-testid="start-timer-card">
                <CardHeader>
                  <CardTitle className="text-lg">Start Timer</CardTitle>
                  <CardDescription>Track time for a new task</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>What are you working on?</Label>
                    <Input
                      placeholder="Task description..."
                      value={timerForm.description}
                      onChange={(e) => setTimerForm(f => ({ ...f, description: e.target.value }))}
                      data-testid="timer-description"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Client (optional)</Label>
                      <Select value={timerForm.client_id || "none"} onValueChange={(v) => setTimerForm(f => ({ ...f, client_id: v === "none" ? "" : v }))}>
                        <SelectTrigger data-testid="timer-client">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No client</SelectItem>
                          {clients.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name || c.company_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Project (optional)</Label>
                      <Select value={timerForm.project_id || "none"} onValueChange={(v) => setTimerForm(f => ({ ...f, project_id: v === "none" ? "" : v }))}>
                        <SelectTrigger data-testid="timer-project">
                          <SelectValue placeholder="Select project" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No project</SelectItem>
                          {projects.filter(p => p.status === "active").map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Hourly Rate (optional)</Label>
                      <Input
                        type="number"
                        placeholder={settings?.default_hourly_rate || "100"}
                        value={timerForm.hourly_rate}
                        onChange={(e) => setTimerForm(f => ({ ...f, hourly_rate: e.target.value }))}
                        data-testid="timer-rate"
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch
                        id="billable"
                        checked={timerForm.is_billable}
                        onCheckedChange={(v) => setTimerForm(f => ({ ...f, is_billable: v }))}
                        data-testid="timer-billable"
                      />
                      <Label htmlFor="billable">Billable</Label>
                    </div>
                    
                    <Button size="lg" onClick={handleStartTimer} data-testid="start-timer-btn">
                      <Play className="h-5 w-5 mr-2" />
                      Start Timer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card data-testid="kpi-today">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Today</p>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {formatHoursMinutes(
                      timeEntries
                        .filter(e => e.date?.split('T')[0] === new Date().toISOString().split('T')[0])
                        .reduce((acc, e) => acc + (e.duration_minutes || 0), 0)
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Logged today</p>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-week">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">This Week</p>
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {utilization ? `${utilization.total_hours.toFixed(1)}h` : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {utilization ? `Target: ${utilization.target_hours}h` : "Loading..."}
                  </p>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-utilization">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Utilization</p>
                    <Target className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {utilization ? `${utilization.utilization_rate.toFixed(0)}%` : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {utilization ? `Target: ${utilization.target_utilization}%` : "Loading..."}
                  </p>
                </CardContent>
              </Card>
              
              <Card data-testid="kpi-unbilled">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">Unbilled</p>
                    <DollarSign className="h-4 w-4 text-[hsl(var(--ff-expense))]" />
                  </div>
                  <p className="text-2xl font-bold mt-2 text-[hsl(var(--ff-expense))]">
                    {unbilledTime ? formatCurrency(unbilledTime.total_unbilled_amount) : "—"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {unbilledTime ? `${unbilledTime.total_unbilled_hours.toFixed(1)}h unbilled` : "Loading..."}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Entries */}
            <Card data-testid="recent-entries">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Recent Time Entries</CardTitle>
                  <CardDescription>Your latest tracked time</CardDescription>
                </div>
                <Button size="sm" onClick={() => openEntryDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Manual Entry
                </Button>
              </CardHeader>
              <CardContent>
                {timeEntries.slice(0, 10).length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Client / Project</TableHead>
                        <TableHead className="text-right">Duration</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[60px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {timeEntries.slice(0, 10).map(entry => (
                        <TableRow key={entry.id} data-testid={`entry-row-${entry.id}`}>
                          <TableCell className="text-sm">
                            {entry.date ? format(parseISO(entry.date), "MMM d") : "-"}
                          </TableCell>
                          <TableCell className="font-medium max-w-[200px] truncate">
                            {entry.description}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {entry.client_name && <span>{entry.client_name}</span>}
                            {entry.client_name && entry.project_name && " / "}
                            {entry.project_name && <span>{entry.project_name}</span>}
                            {!entry.client_name && !entry.project_name && "-"}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatHoursMinutes(entry.duration_minutes)}
                          </TableCell>
                          <TableCell className="text-right">
                            {entry.is_billable ? formatCurrency(entry.billed_amount) : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[entry.status]} variant="secondary">
                              {entry.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEntryDialog(entry)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteEntry(entry.id)} className="text-red-600">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="h-10 w-10 mx-auto mb-3" />
                    <p>No time entries yet</p>
                    <p className="text-sm">Start tracking time or add a manual entry</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time Entries Tab */}
          <TabsContent value="entries" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => openEntryDialog()} data-testid="add-entry-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Time Entry
              </Button>
            </div>
            
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead className="text-center">Billable</TableHead>
                      <TableHead className="text-right">Duration</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {timeEntries.map(entry => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-sm">
                          {entry.date ? format(parseISO(entry.date), "MMM d, yyyy") : "-"}
                        </TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {entry.description}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {entry.client_name || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {entry.project_name || "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {entry.is_billable ? (
                            <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {entry.duration_hours?.toFixed(2)}h
                        </TableCell>
                        <TableCell className="text-right">
                          {entry.is_billable ? formatCurrency(entry.billed_amount) : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[entry.status]} variant="secondary">
                            {entry.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEntryDialog(entry)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteEntry(entry.id)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {timeEntries.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="h-10 w-10 mx-auto mb-3" />
                    <p>No time entries yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => openProjectDialog()} data-testid="add-project-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Project
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map(project => (
                <Card key={project.id} data-testid={`project-card-${project.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold">{project.name}</h3>
                        {project.client_name && (
                          <p className="text-sm text-muted-foreground">{project.client_name}</p>
                        )}
                      </div>
                      <Badge className={PROJECT_STATUS_COLORS[project.status]} variant="secondary">
                        {project.status.replace("_", " ")}
                      </Badge>
                    </div>
                    
                    {project.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{project.description}</p>
                    )}
                    
                    <div className="mt-4 space-y-2">
                      {project.budget_hours && (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Hours</span>
                            <span>{project.logged_hours?.toFixed(1)} / {project.budget_hours}h</span>
                          </div>
                          <Progress 
                            value={Math.min((project.logged_hours / project.budget_hours) * 100, 100)} 
                            className="h-2"
                          />
                        </div>
                      )}
                      
                      {project.budget_amount && (
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Budget</span>
                            <span>{formatCurrency(project.logged_amount)} / {formatCurrency(project.budget_amount)}</span>
                          </div>
                          <Progress 
                            value={Math.min((project.logged_amount / project.budget_amount) * 100, 100)} 
                            className="h-2"
                          />
                        </div>
                      )}
                      
                      {!project.budget_hours && !project.budget_amount && (
                        <div className="text-sm text-muted-foreground">
                          <span>{project.logged_hours?.toFixed(1)}h logged</span>
                          {project.hourly_rate && <span> • ${project.hourly_rate}/hr</span>}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => openProjectDialog(project)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteProject(project.id)}>
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {projects.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    <FolderKanban className="h-10 w-10 mx-auto mb-3" />
                    <p>No projects yet</p>
                    <Button variant="outline" className="mt-4" onClick={() => openProjectDialog()}>
                      Create Your First Project
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            {utilization && (
              <>
                {/* Period Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Weekly Utilization</CardTitle>
                    <CardDescription>
                      {format(parseISO(utilization.period_start), "MMM d")} - {format(parseISO(utilization.period_end), "MMM d, yyyy")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Total Hours</p>
                        <p className="text-2xl font-bold">{utilization.total_hours.toFixed(1)}h</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Billable Hours</p>
                        <p className="text-2xl font-bold text-green-600">{utilization.billable_hours.toFixed(1)}h</p>
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Utilization Rate</p>
                        <p className="text-2xl font-bold">{utilization.utilization_rate.toFixed(0)}%</p>
                        <Progress value={utilization.utilization_rate} className="h-2 mt-2" />
                      </div>
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Billable Amount</p>
                        <p className="text-2xl font-bold">{formatCurrency(utilization.billable_amount)}</p>
                      </div>
                    </div>
                    
                    {/* Daily Breakdown Chart */}
                    {utilization.daily_breakdown?.length > 0 && (
                      <div className="h-[200px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={utilization.daily_breakdown}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tickFormatter={(d) => format(parseISO(d), "EEE")} />
                            <YAxis />
                            <Tooltip 
                              formatter={(value) => [`${value.toFixed(1)}h`, ""]}
                              labelFormatter={(d) => format(parseISO(d), "EEEE, MMM d")}
                            />
                            <Bar dataKey="billable_hours" name="Billable" fill="hsl(var(--ff-income))" />
                            <Bar dataKey="hours" name="Total" fill="hsl(var(--ff-primary))" opacity={0.3} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* By Client */}
                {utilization.by_client?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Hours by Client</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="h-[200px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={utilization.by_client.filter(c => c.hours > 0)}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="hours"
                                nameKey="client_id"
                                label={({ client_id, percent }) => `${(percent * 100).toFixed(0)}%`}
                              >
                                {utilization.by_client.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => `${value.toFixed(1)}h`} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Client</TableHead>
                              <TableHead className="text-right">Hours</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {utilization.by_client.slice(0, 5).map((c, i) => (
                              <TableRow key={c.client_id}>
                                <TableCell className="font-medium">
                                  {c.client_id === "internal" ? "Internal / No Client" : `Client ${i + 1}`}
                                </TableCell>
                                <TableCell className="text-right">{c.hours.toFixed(1)}h</TableCell>
                                <TableCell className="text-right">{formatCurrency(c.amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Unbilled Tab */}
          <TabsContent value="unbilled" className="space-y-4">
            {unbilledTime && (
              <>
                <Card className="border-[hsl(var(--ff-expense))]/50 bg-[hsl(var(--ff-expense))]/5">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-medium">Total Unbilled</p>
                        <p className="text-3xl font-bold text-[hsl(var(--ff-expense))]">
                          {formatCurrency(unbilledTime.total_unbilled_amount)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {unbilledTime.total_unbilled_hours.toFixed(1)} hours across {unbilledTime.by_client?.length || 0} clients
                        </p>
                      </div>
                      <Receipt className="h-12 w-12 text-[hsl(var(--ff-expense))]" />
                    </div>
                  </CardContent>
                </Card>

                {unbilledTime.by_client?.map(client => (
                  <Card key={client.client_id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{client.client_name}</CardTitle>
                          <CardDescription>
                            {client.total_hours.toFixed(1)} hours • {formatCurrency(client.total_amount)}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Project</TableHead>
                            <TableHead className="text-right">Hours</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {client.entries?.map(entry => (
                            <TableRow key={entry.id}>
                              <TableCell className="text-sm">{entry.date}</TableCell>
                              <TableCell className="font-medium max-w-[200px] truncate">{entry.description}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{entry.project_name || "-"}</TableCell>
                              <TableCell className="text-right">{entry.hours}h</TableCell>
                              <TableCell className="text-right">{formatCurrency(entry.amount)}</TableCell>
                              <TableCell>
                                <Badge className={STATUS_COLORS[entry.status]} variant="secondary">
                                  {entry.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}

                {unbilledTime.by_client?.length === 0 && (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      <CheckCircle className="h-10 w-10 mx-auto mb-3 text-green-500" />
                      <p>No unbilled time entries!</p>
                      <p className="text-sm">All billable time has been invoiced.</p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Time Entry Dialog */}
      <Dialog open={entryDialog.open} onOpenChange={(open) => setEntryDialog({ open, entry: open ? entryDialog.entry : null })}>
        <DialogContent data-testid="entry-dialog">
          <DialogHeader>
            <DialogTitle>{entryDialog.entry?.id ? "Edit Time Entry" : "Add Time Entry"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={entryForm.description}
                onChange={(e) => setEntryForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What did you work on?"
                data-testid="entry-description"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={entryForm.date}
                  onChange={(e) => setEntryForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="entry-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={entryForm.duration_minutes}
                  onChange={(e) => setEntryForm(f => ({ ...f, duration_minutes: e.target.value }))}
                  placeholder="60"
                  data-testid="entry-duration"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={entryForm.client_id || "none"} onValueChange={(v) => setEntryForm(f => ({ ...f, client_id: v === "none" ? "" : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No client</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name || c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Project</Label>
                <Select value={entryForm.project_id || "none"} onValueChange={(v) => setEntryForm(f => ({ ...f, project_id: v === "none" ? "" : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hourly Rate</Label>
                <Input
                  type="number"
                  value={entryForm.hourly_rate}
                  onChange={(e) => setEntryForm(f => ({ ...f, hourly_rate: e.target.value }))}
                  placeholder={settings?.default_hourly_rate || "100"}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  id="entry-billable"
                  checked={entryForm.is_billable}
                  onCheckedChange={(v) => setEntryForm(f => ({ ...f, is_billable: v }))}
                />
                <Label htmlFor="entry-billable">Billable</Label>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={entryForm.notes}
                onChange={(e) => setEntryForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryDialog({ open: false, entry: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveEntry} data-testid="save-entry-btn">
              {entryDialog.entry?.id ? "Update Entry" : "Create Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Dialog */}
      <Dialog open={projectDialog.open} onOpenChange={(open) => setProjectDialog({ open, project: open ? projectDialog.project : null })}>
        <DialogContent data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle>{projectDialog.project?.id ? "Edit Project" : "Add Project"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Project Name *</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Project name"
                data-testid="project-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={projectForm.client_id || "none"} onValueChange={(v) => setProjectForm(f => ({ ...f, client_id: v === "none" ? "" : v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No client</SelectItem>
                    {clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name || c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={projectForm.status} onValueChange={(v) => setProjectForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={projectForm.description}
                onChange={(e) => setProjectForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Project description..."
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Budget Hours</Label>
                <Input
                  type="number"
                  value={projectForm.budget_hours}
                  onChange={(e) => setProjectForm(f => ({ ...f, budget_hours: e.target.value }))}
                  placeholder="e.g., 100"
                />
              </div>
              <div className="space-y-2">
                <Label>Budget Amount</Label>
                <Input
                  type="number"
                  value={projectForm.budget_amount}
                  onChange={(e) => setProjectForm(f => ({ ...f, budget_amount: e.target.value }))}
                  placeholder="e.g., 10000"
                />
              </div>
              <div className="space-y-2">
                <Label>Hourly Rate</Label>
                <Input
                  type="number"
                  value={projectForm.hourly_rate}
                  onChange={(e) => setProjectForm(f => ({ ...f, hourly_rate: e.target.value }))}
                  placeholder={settings?.default_hourly_rate || "100"}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProjectDialog({ open: false, project: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveProject} data-testid="save-project-btn">
              {projectDialog.project?.id ? "Update Project" : "Create Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent data-testid="settings-dialog">
          <DialogHeader>
            <DialogTitle>Time Tracking Settings</DialogTitle>
            <DialogDescription>Configure your default preferences</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default Hourly Rate</Label>
                <Input
                  type="number"
                  value={settingsForm.default_hourly_rate}
                  onChange={(e) => setSettingsForm(f => ({ ...f, default_hourly_rate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Target Weekly Hours</Label>
                <Input
                  type="number"
                  value={settingsForm.target_weekly_hours}
                  onChange={(e) => setSettingsForm(f => ({ ...f, target_weekly_hours: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Target Utilization %</Label>
                <Input
                  type="number"
                  value={settingsForm.target_utilization_percent}
                  onChange={(e) => setSettingsForm(f => ({ ...f, target_utilization_percent: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Rounding (minutes)</Label>
                <Select 
                  value={settingsForm.rounding_increment.toString()} 
                  onValueChange={(v) => setSettingsForm(f => ({ ...f, rounding_increment: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 minute</SelectItem>
                    <SelectItem value="5">5 minutes</SelectItem>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                id="default-billable"
                checked={settingsForm.default_billable}
                onCheckedChange={(v) => setSettingsForm(f => ({ ...f, default_billable: v }))}
              />
              <Label htmlFor="default-billable">Default to billable</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} data-testid="save-settings-btn">
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
